import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const IMAGES = {
  projects: "https://customer-assets.emergentagent.com/job_7952493e-ecb0-4483-a1aa-f19d77930300/artifacts/k61v6idi_Project.png",
  estimator: "https://customer-assets.emergentagent.com/job_55de5930-e1fe-4683-9c65-c6190d26c152/artifacts/7hgjw8f3_ProjectEstimator.png",
  tools: "https://customer-assets.emergentagent.com/job_delivery-tools-suite/artifacts/b9j4okv8_Tools.png",
  delivery: "https://customer-assets.emergentagent.com/job_7952493e-ecb0-4483-a1aa-f19d77930300/artifacts/ekpk2yty_Delivery.png",
  dmlBuilder: "https://customer-assets.emergentagent.com/job_7952493e-ecb0-4483-a1aa-f19d77930300/artifacts/rtc93b69_DML%20Builder%20Logo2.png",
  brexValidation: "https://customer-assets.emergentagent.com/job_7952493e-ecb0-4483-a1aa-f19d77930300/artifacts/hd7tcvfm_BREXValidation.png",
  brexBuilder: "https://customer-assets.emergentagent.com/job_4dda6c2c-c6cf-494a-b578-b0548b3af0ae/artifacts/d5198eca_BREXBuilder.png",
  author: "https://customer-assets.emergentagent.com/job_55de5930-e1fe-4683-9c65-c6190d26c152/artifacts/9fs2w7fm_Author.png",
  pdfPublish: "https://customer-assets.emergentagent.com/job_55de5930-e1fe-4683-9c65-c6190d26c152/artifacts/49fa80il_PDFPublish.png",
  steCheck: "https://customer-assets.emergentagent.com/job_55de5930-e1fe-4683-9c65-c6190d26c152/artifacts/vfvqnrzk_STECheck.png",
  resources: "/assets/resources-icon.png",
  users: "/assets/users-icon.png",
  productConfig: "/assets/product-config-icon.png",
  conversion: "https://customer-assets.emergentagent.com/job_55de5930-e1fe-4683-9c65-c6190d26c152/artifacts/tl94uv0a_Conversion.png"
};

const DashboardPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const [allowedTools, setAllowedTools] = useState(null);

  useEffect(() => {
    const fetchAllowed = async () => {
      try {
        const res = await fetch(`${API}/api/product-configs/my-tools`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setAllowedTools(data.tools);
        }
      } catch {}
    };
    if (token) fetchAllowed();
  }, [token]);

  const estimatorAllowed = !allowedTools || allowedTools.includes('project-estimator');

  const quickActions = [
    ...(estimatorAllowed ? [{
      title: 'Project Estimator',
      description: 'Estimate S1000D project costs, resources, and timelines',
      image: IMAGES.estimator,
      path: '/dashboard/estimator'
    }] : []),
    ...(!isAdmin ? [
      {
        title: 'Projects',
        description: 'Manage your S1000D projects',
        image: IMAGES.projects,
        path: '/dashboard/projects'
      },
    ] : []),
    {
      title: 'Tools',
      description: isAdmin ? 'Test Delivery, DML Builder, and BREX Validation' : 'Access Delivery, DML Builder, and BREX Validation',
      image: IMAGES.tools,
      path: '/dashboard/tools'
    },
    {
      title: 'Resources',
      description: isAdmin ? 'Upload and manage PDF resources' : 'Download available resources',
      image: IMAGES.resources,
      path: '/dashboard/resources'
    },
    ...(isAdmin ? [{
      title: 'Users',
      description: 'Manage user accounts and roles',
      image: IMAGES.users,
      path: '/dashboard/users'
    },
    {
      title: 'Product Config',
      description: 'Create and manage tool configurations',
      image: IMAGES.productConfig,
      path: '/dashboard/product-config'
    }] : [])
  ];

  const allRecentTools = [
    { name: 'Delivery', key: 'delivery', image: IMAGES.delivery, path: '/dashboard/tools/delivery' },
    { name: 'DML Builder', key: 'dml-builder', image: IMAGES.dmlBuilder, path: '/dashboard/tools/dml-builder' },
    { name: 'BREX Validation', key: 'brex-validation', image: IMAGES.brexValidation, path: '/dashboard/tools/brex-validation' },
    { name: 'BREX Builder', key: 'brex-builder', image: IMAGES.brexBuilder, path: '/dashboard/tools/brex-builder' },
    { name: 'Author', key: 'author', image: IMAGES.author, path: '/dashboard/tools/author' },
    { name: 'PDF Publish', key: 'pdf-publish', image: IMAGES.pdfPublish, path: '/dashboard/tools/pdf-publish' },
    { name: 'STE Check', key: 'ste-check', image: IMAGES.steCheck, path: '/dashboard/tools/ste-check' },
    { name: 'Conversion', key: 'conversion', image: IMAGES.conversion, path: '/dashboard/tools/conversion' }
  ];
  const recentTools = allRecentTools.filter(t => !allowedTools || allowedTools.includes(t.key));

  return (
    <div className="space-y-10" data-testid="dashboard-page">
      {/* Welcome Section */}
      <div className="space-y-2">
        <h1 
          className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900"
          style={{ fontFamily: 'Outfit, sans-serif' }}
          data-testid="welcome-heading"
        >
          Welcome, {user?.first_name}
        </h1>
        <p className="text-lg text-slate-600">
          What would you like to work on today?
        </p>
      </div>

      {/* Quick Actions */}
      <div className={`grid grid-cols-1 gap-6 ${quickActions.length <= 3 ? 'md:grid-cols-3' : 'md:grid-cols-2 lg:grid-cols-4'}`}>
        {quickActions.map((action) => (
          <Card 
            key={action.title}
            className="group relative overflow-hidden bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer"
            onClick={() => navigate(action.path)}
            data-testid={`quick-action-${action.title.toLowerCase()}`}
          >
            <CardHeader className="pb-2">
              <div className="w-20 h-20 mb-4 group-hover:scale-110 transition-transform">
                <img 
                  src={action.image} 
                  alt={action.title}
                  className="w-full h-full object-contain drop-shadow-lg"
                />
              </div>
              <CardTitle className="text-2xl flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {action.title}
                <ArrowRight className="w-5 h-5 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
              </CardTitle>
              <CardDescription className="text-base text-slate-500">
                {action.description}
              </CardDescription>
            </CardHeader>
          </Card>
        ))}
      </div>

      {/* Recent Tools Section with Images */}
      <div className="space-y-4">
        <h2 
          className="text-2xl font-semibold text-slate-900"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Quick Access to Tools
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
          {recentTools.map((tool) => (
            <Card 
              key={tool.name}
              className="group bg-white border border-slate-200 hover:border-violet-200 hover:shadow-lg transition-all cursor-pointer overflow-hidden"
              onClick={() => navigate(tool.path)}
              data-testid={`tool-shortcut-${tool.name.toLowerCase().replace(' ', '-')}`}
            >
              <CardContent className="flex flex-col items-center gap-4 p-6">
                <div className="w-20 h-20 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <img 
                    src={tool.image} 
                    alt={tool.name}
                    className="w-full h-full object-contain drop-shadow-md"
                  />
                </div>
                <span className="font-semibold text-slate-700 group-hover:text-violet-600 transition-colors">
                  {tool.name}
                </span>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Info Banner - Simplified */}
      <Card className="bg-gradient-to-r from-slate-900 to-slate-800 border-0 text-white">
        <CardContent className="p-6">
          <h3 className="text-xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            MySpecWorkspace
          </h3>
          <p className="text-slate-300 mt-1">
            All your specs. One workspace.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
